import { ChangeDetectorRef, Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { ThemeService } from '@services/theme.service';
import { BaseComponent } from '@shared/components/base-component';
import Utils from '@shared/providers/utils';
import { editor } from 'monaco-editor';
import { DiffEditorModel } from 'ngx-monaco-editor-v2';
import IStandaloneDiffEditorConstructionOptions = editor.IStandaloneDiffEditorConstructionOptions;

@Component({
  selector: 'app-code-diff-viewer',
  templateUrl: './code-diff-viewer.component.html',
  standalone: false,
})
export class CodeDiffViewerComponent<T> extends BaseComponent {
  private readonly themeService = inject(ThemeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly userOptions = signal<IStandaloneDiffEditorConstructionOptions>({});

  readonly codeOptions = computed<IStandaloneDiffEditorConstructionOptions>(() => ({
    minimap: { enabled: false },
    scrollbar: { alwaysConsumeMouseWheel: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    theme: this.themeService.theme() === 'dark' ? 'vs-dark' : 'vs',
    ...this.userOptions(),
  }));

  protected originalModel: DiffEditorModel = {
    code: '{}',
    language: 'json',
  };
  protected modifiedModel = {
    code: '{}',
    language: 'json',
  };
  @Input() modelType: 'json' | 'string' = 'json';
  @Input() cssClass: string | string[] | Record<string, unknown> = {};
  @Input() cssStyle: Record<string, unknown> = {};
  @Output() modifiedCodeChange = new EventEmitter<T | null>();
  @Output() jsonParserErrorChange = new EventEmitter<string | undefined>();

  @Input()
  set originalCode(code: T | string | null) {
    this.originalModel = {
      ...this.originalModel,
      code: this.formatCode(code),
    };
    this.cdr.markForCheck();
  }

  @Input()
  set modifiedCode(code: T | string | null) {
    this.modifiedModel = {
      ...this.modifiedModel,
      code: this.formatCode(code),
    };
    this.cdr.markForCheck();
  }

  @Input()
  set options(options: IStandaloneDiffEditorConstructionOptions) {
    this.userOptions.set(options);
  }

  format() {
    this.originalCode = this.formatCode(this.originalModel.code);
    this.modifiedCode = this.formatCode(this.modifiedModel.code);
  }

  private formatCode(code: T | string | null): string {
    if (code === null) {
      return JSON.stringify(null, null, 2);
    }
    if (typeof code === 'object') {
      const formattedCode = Utils.sortObjectKeysDeep(code);
      return JSON.stringify(formattedCode, null, 2);
    }
    const parsedCode = JSON.parse(code as string);
    const formattedCode = Utils.sortObjectKeysDeep(parsedCode);
    return JSON.stringify(formattedCode, null, 2);
  }

  protected onEditorInit(editor: editor.IStandaloneDiffEditor) {
    if (!editor) {
      return;
    }
    editor.getModifiedEditor().onDidChangeModelContent(() => {
      const newCode = editor.getModel()?.modified.getValue();
      if (!newCode) {
        this.modifiedCodeChange.next(null);
        return;
      }
      let jsonParserErrror: string | undefined = undefined;
      try {
        const parsedNewCode = JSON.parse(newCode) as T;
        this.modifiedCodeChange.next(parsedNewCode);
      } catch (err: unknown) {
        if (err instanceof SyntaxError) {
          jsonParserErrror = err.message;
        }
      }
      this.jsonParserErrorChange.next(jsonParserErrror);
    });
  }
}
